import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DiscoveredFeed, FeedDto, OpmlImportResult } from '@b4b/shared';
import {
  useFeeds,
  useDiscover,
  useSubscribe,
  useUnsubscribe,
  useUpdateSubscription,
} from '../api/queries.js';
import { useQueryClient } from '@tanstack/react-query';
import { describeDiscoverError } from '../lib/discoverError.js';
import RecommendedPacks from '../components/RecommendedPacks.js';

export default function Sources() {
  const { t } = useTranslation();
  const feeds = useFeeds();
  const unsubscribe = useUnsubscribe();
  const updateSub = useUpdateSubscription();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState<OpmlImportResult | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, FeedDto[]>();
    for (const feed of feeds.data ?? []) {
      const key = feed.category ?? '';
      const list = map.get(key) ?? [];
      list.push(feed);
      map.set(key, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [feeds.data]);

  const importOpml = async (file: File) => {
    const text = await file.text();
    const res = await fetch('/api/feeds/import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ opml: text }),
      credentials: 'same-origin',
    });
    if (res.ok) {
      setImportResult((await res.json()) as OpmlImportResult);
      void qc.invalidateQueries({ queryKey: ['feeds'] });
    }
  };

  return (
    <div className="space-y-6 px-4 pb-4 pt-20">
      <h1 className="text-2xl font-bold">{t('sources.title')}</h1>

      <AddSource />

      <div className="flex gap-3 text-sm">
        <button onClick={() => fileInput.current?.click()} className="text-accent hover:underline">
          {t('sources.importOpml')}
        </button>
        <a href="/api/feeds/export" download className="text-accent hover:underline">
          {t('sources.exportOpml')}
        </a>
        <input
          ref={fileInput}
          type="file"
          accept=".opml,.xml,text/xml"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void importOpml(file);
            e.target.value = '';
          }}
        />
      </div>
      {importResult && (
        <p className="text-sm text-neutral-500">
          {t('sources.importResult', {
            imported: importResult.imported,
            failed: importResult.failed.length,
          })}
        </p>
      )}

      {grouped.map(([category, list]) => (
        <section key={category || 'uncategorized'} data-testid="subscriptions">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-neutral-500">
            {category || t('sources.uncategorized')}
          </h2>
          <ul className="space-y-2">
            {list.map((feed) => (
              <li
                key={feed.id}
                className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900"
              >
                {feed.faviconUrl ? (
                  <img
                    src={feed.faviconUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="h-6 w-6 rounded"
                    onError={(e) => ((e.target as HTMLImageElement).style.visibility = 'hidden')}
                  />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded bg-neutral-200 text-xs dark:bg-neutral-700">
                    {(feed.title ?? '?').slice(0, 1)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{feed.title ?? feed.url}</p>
                  <p className="text-xs text-neutral-500">
                    {t('sources.articlesPerWeek', { count: feed.articlesLast7Days ?? 0 })}
                    {feed.failCount >= 10 && (
                      <span className="ml-2 text-red-500">⚠ {t('sources.feedError')}</span>
                    )}
                  </p>
                </div>
                <input
                  defaultValue={feed.category ?? ''}
                  placeholder={t('sources.categoryPlaceholder')}
                  onBlur={(e) => {
                    const value = e.target.value.trim() || null;
                    if (value !== feed.category) {
                      updateSub.mutate({ feedId: feed.id, category: value });
                    }
                  }}
                  className="w-24 rounded-lg border border-neutral-200 bg-transparent px-2 py-1 text-xs dark:border-neutral-700 sm:w-32"
                />
                <button
                  onClick={() => unsubscribe.mutate(feed.id)}
                  aria-label={t('sources.unsubscribe')}
                  className="min-h-[44px] min-w-[44px] rounded-lg text-neutral-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <RecommendedPacks />
    </div>
  );
}

function AddSource() {
  const { t } = useTranslation();
  const discover = useDiscover();
  const subscribe = useSubscribe();
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('');
  const [results, setResults] = useState<DiscoveredFeed[] | null>(null);

  const run = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setResults(null);
    discover.mutate(url.trim(), { onSuccess: setResults });
  };

  return (
    <div className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <form onSubmit={run} className="flex gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={t('sources.addPlaceholder')}
          className="flex-1 rounded-xl border border-neutral-300 bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-950"
        />
        <button
          type="submit"
          disabled={discover.isPending}
          className="rounded-xl bg-accent px-4 py-2 font-medium text-white hover:bg-accent-strong disabled:opacity-50"
        >
          {discover.isPending ? t('sources.discovering') : t('sources.discover')}
        </button>
      </form>

      {results !== null && results.length === 0 && (
        <p className="text-sm text-neutral-500">{t('sources.noFeedsFound')}</p>
      )}
      {discover.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">{describeDiscoverError(discover.error, t)}</p>
      )}
      {results?.map((found) => (
        <div
          key={found.feedUrl}
          className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-700"
        >
          <p className="font-medium">{found.title ?? found.feedUrl}</p>
          <p className="truncate text-xs text-neutral-500">{found.feedUrl}</p>
          <ul className="mt-2 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
            {found.preview.slice(0, 3).map((p, i) => (
              <li key={i} className="truncate">
                · {p.title}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2">
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t('sources.categoryPlaceholder')}
              className="w-32 rounded-lg border border-neutral-200 bg-transparent px-2 py-1 text-sm dark:border-neutral-700"
            />
            <button
              onClick={() =>
                subscribe.mutate(
                  { feedUrl: found.feedUrl, category: category.trim() || undefined },
                  {
                    onSuccess: () => {
                      setResults(null);
                      setUrl('');
                    },
                  },
                )
              }
              className="rounded-lg bg-accent px-3 py-1 text-sm font-medium text-white hover:bg-accent-strong"
            >
              {t('sources.subscribe')}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
